/**
 * The structure toolkit: everything a structure file needs to describe a
 * building, and nothing about where structures are placed (see index.ts).
 *
 *  - `Builder` writes blocks in a piece's own frame: u runs across the piece,
 *    v into it, y up; the piece's rotation maps that onto the world, including
 *    the facing of chests, furnaces, doors and beds.
 *  - `blueprint` turns ASCII layers into something a Builder can stamp, the
 *    way vanilla stamps its NBT templates — draw the building, don't code it.
 *  - `block('Name')` looks a block up by its display name, so a structure can
 *    use any block without blocks.ts exporting a constant for it.
 *
 * Writes outside the chunk being generated are dropped (every chunk a piece
 * touches rebuilds it and keeps its own part), so builders never need to clip.
 */

import {
  AIR, BED_FOOT_ID, BED_HEAD_ID, BLOCKS, CHEST_FACINGS, DOOR_KINDS, doorId, FURNACE_FACINGS, IS_OPAQUE,
} from '../../blocks';
import { mulberry32 } from '../../noise';
import type { LootKind } from '../../game/loot';
import type { TerrainGenerator } from '../terrain';
import { type GenCtx, getBlock, setBlock } from '../ctx';

// --- randomness ------------------------------------------------------------------

export type Rng = () => number;
/** Integer in [0, n). */
export const int = (rnd: Rng, n: number): number => (rnd() * n) | 0;
/** True with probability p. */
export const chance = (rnd: Rng, p: number): boolean => rnd() < p;
/** One element of a list. */
export const pick = <T>(rnd: Rng, list: readonly T[]): T => list[int(rnd, list.length)];
/** A fresh seed for a sub-generator. */
export const seedOf = (rnd: Rng): number => int(rnd, 1e9);

// --- blocks by name --------------------------------------------------------------

const BY_NAME = new Map<string, number>();
for (const b of BLOCKS) if (b && !BY_NAME.has(b.name)) BY_NAME.set(b.name, b.id);

/** Block id by its display name ('Mossy Stone Bricks'); the first id of a
 *  multi-state block (the default-facing chest, the closed door). Throws on
 *  an unknown name, so a typo fails at load instead of building air. */
export function block(name: string): number {
  const id = BY_NAME.get(name);
  if (id === undefined) throw new Error(`structures: no block named '${name}'`);
  return id;
}

// --- structure types and pieces -------------------------------------------------

/** One building (or corridor, or street) of a structure. */
export interface Piece {
  /** World footprint, inclusive. Builders may overhang it by up to PIECE_OVERHANG. */
  x0: number; z0: number; x1: number; z1: number;
  /** Ground level the piece stands on. */
  y: number;
  /** Level the terrain under the footprint before building (vanilla's "beard"). */
  flat: boolean;
  build(ctx: GenCtx): void;
  /** Loot table of the chests inside this footprint (default: the plain overworld table). */
  loot?: LootKind;
}

/** How far a builder may write outside its piece's footprint (roof eaves, cleared margins). */
export const PIECE_OVERHANG = 3;

/**
 * A kind of structure. Placement is vanilla's random spread: the world is cut
 * into `spacing`-chunk regions with one candidate chunk each, `separation`
 * chunks clear of the next region's (the numbers vanilla's structure sets use).
 */
export interface StructureType {
  name: string;
  spacing: number;
  separation: number;
  salt: number;
  /** How far (chunks) a piece can reach from the start chunk. */
  reach: number;
  /** False for hidden / underground kinds (no debug compass line). */
  hud?: boolean;
  /**
   * Fixed placement instead of random spread (strongholds sit on rings): the
   * start chunk inside region (rx, rz) of `spacing` chunks, or null for none.
   */
  site?(seed: number, rx: number, rz: number): { cx: number; cz: number } | null;
  /** The pieces for a start at world (x, z), or null when the spot is unsuitable. */
  generate(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null;
}

// --- frames and facings ----------------------------------------------------------

/** World direction of local +u and +v for each of the four rotations. */
const UX = [1, 0, -1, 0], UZ = [0, 1, 0, -1];
const VX = [0, -1, 0, 1], VZ = [1, 0, -1, 0];

/** Local facings, in the order block facings use: 0 = +v, 1 = +u, 2 = -v, 3 = -u.
 *  With rotation 0 they are the world facings (+Z, +X, -Z, -X). */
export const F = { PV: 0, PU: 1, NV: 2, NU: 3 } as const;
const LOCAL_DIR: ReadonlyArray<readonly [number, number]> = [[0, 1], [1, 0], [0, -1], [-1, 0]];

/** World footprint of a w x d piece at (x, z) with rotation `rot`. */
export function footprint(x: number, z: number, rot: number, w: number, d: number): {
  x0: number; z0: number; x1: number; z1: number;
} {
  const ax = x + (w - 1) * UX[rot] + (d - 1) * VX[rot];
  const az = z + (w - 1) * UZ[rot] + (d - 1) * VZ[rot];
  return { x0: Math.min(x, ax), z0: Math.min(z, az), x1: Math.max(x, ax), z1: Math.max(z, az) };
}

/** World point of local (u, v) for a piece at (x, z) with rotation `rot`. */
export function toWorld(x: number, z: number, rot: number, u: number, v: number): [number, number] {
  return [x + u * UX[rot] + v * VX[rot], z + u * UZ[rot] + v * VZ[rot]];
}

/**
 * A piece of `w` x `d` columns with its local origin at world (x, z), turned
 * by `rot` quarter turns. `build` gets a Builder in the piece's frame with its
 * own random stream (seeded, so every chunk rebuilds it identically).
 */
export function piece(
  x: number, z: number, rot: number, w: number, d: number, y: number, flat: boolean, seed: number,
  build: (b: Builder) => void,
): Piece {
  return {
    ...footprint(x, z, rot, w, d), y, flat,
    build(ctx) { build(new Builder(ctx, x, z, rot, y, mulberry32(seed))); },
  };
}

/**
 * A w x d piece centred on world (cx, cz) whatever its rotation — what a
 * single-building structure wants, so turning it never swings the building
 * away from its start chunk (and out of the chunks that build it).
 */
export function centredPiece(
  cx: number, cz: number, rot: number, w: number, d: number, y: number, flat: boolean, seed: number,
  build: (b: Builder) => void,
): Piece {
  const [x, z] = toWorld(cx, cz, rot, -(w >> 1), -(d >> 1));
  return piece(x, z, rot, w, d, y, flat, seed, build);
}

/** A piece that writes in world coordinates directly (streets, corridors). */
export function worldPiece(
  x0: number, z0: number, x1: number, z1: number, y: number, build: (ctx: GenCtx) => void,
): Piece {
  return { x0, z0, x1, z1, y, flat: false, build };
}

// --- the builder -----------------------------------------------------------------

/** A block to write, or 'keep' to leave the world as it is. */
export type Fill = number | ((b: Builder, u: number, y: number, v: number) => number | void);

export class Builder {
  constructor(
    readonly ctx: GenCtx,
    private readonly ox: number, private readonly oz: number, readonly rot: number,
    /** The piece's ground level. */
    readonly y: number,
    readonly rnd: Rng,
  ) {}

  private wx(u: number, v: number): number { return this.ox + u * UX[this.rot] + v * VX[this.rot]; }
  private wz(u: number, v: number): number { return this.oz + u * UZ[this.rot] + v * VZ[this.rot]; }

  /** Block at a local position (-1 outside the chunk being generated). */
  get(u: number, y: number, v: number): number { return getBlock(this.ctx, this.wx(u, v), y, this.wz(u, v)); }

  /** Write one block; a function fill is called for its id (or writes itself). */
  set(u: number, y: number, v: number, fill: Fill): void {
    const id = typeof fill === 'number' ? fill : fill(this, u, y, v);
    if (typeof id === 'number') setBlock(this.ctx, this.wx(u, v), y, this.wz(u, v), id);
  }

  /** Fill a box (inclusive corners, any order). */
  fill(u0: number, y0: number, v0: number, u1: number, y1: number, v1: number, fill: Fill): void {
    for (let y = Math.min(y0, y1); y <= Math.max(y0, y1); y++) {
      for (let v = Math.min(v0, v1); v <= Math.max(v0, v1); v++) {
        for (let u = Math.min(u0, u1); u <= Math.max(u0, u1); u++) this.set(u, y, v, fill);
      }
    }
  }

  /** The four side walls of a box (no floor or ceiling). */
  walls(u0: number, y0: number, v0: number, u1: number, y1: number, v1: number, fill: Fill): void {
    for (let y = y0; y <= y1; y++) {
      for (let u = u0; u <= u1; u++) { this.set(u, y, v0, fill); this.set(u, y, v1, fill); }
      for (let v = v0 + 1; v < v1; v++) { this.set(u0, y, v, fill); this.set(u1, y, v, fill); }
    }
  }

  /** A hollow box: `shell` on every face, `inside` (default air) within. */
  room(u0: number, y0: number, v0: number, u1: number, y1: number, v1: number, shell: Fill, inside: Fill = AIR): void {
    for (let y = y0; y <= y1; y++) {
      for (let v = v0; v <= v1; v++) {
        for (let u = u0; u <= u1; u++) {
          const edge = u === u0 || u === u1 || v === v0 || v === v1 || y === y0 || y === y1;
          this.set(u, y, v, edge ? shell : inside);
        }
      }
    }
  }

  /** Fill downward from y until solid ground, so foundations never float. */
  foundation(u: number, y: number, v: number, fill: Fill, max = 12): void {
    for (let k = 0; k < max; k++) {
      const c = this.get(u, y - k, v);
      if (c > 0 && IS_OPAQUE[c] === 1) break;
      this.set(u, y - k, v, fill);
    }
  }

  /** Foundation under every column of a rectangle. */
  foundations(u0: number, v0: number, u1: number, v1: number, y: number, fill: Fill, max = 12): void {
    for (let v = v0; v <= v1; v++) for (let u = u0; u <= u1; u++) this.foundation(u, y, v, fill, max);
  }

  /** World facing of a local facing (see F). */
  facing(local: number): 0 | 1 | 2 | 3 {
    const [du, dv] = LOCAL_DIR[local & 3];
    const x = du * UX[this.rot] + dv * VX[this.rot], z = du * UZ[this.rot] + dv * VZ[this.rot];
    return z > 0 ? 0 : x > 0 ? 1 : z < 0 ? 2 : 3;
  }

  /** A chest whose latch faces local `f`. */
  chest(u: number, y: number, v: number, f: number): void { this.set(u, y, v, CHEST_FACINGS[this.facing(f)]); }

  /** A furnace whose front faces local `f`. */
  furnace(u: number, y: number, v: number, f: number): void { this.set(u, y, v, FURNACE_FACINGS[this.facing(f)]); }

  /** Both halves of a closed door, as if placed by someone looking along local
   *  `f` (it sits on their side of the cell); `hinge` 0 = their left. */
  door(kind: string, u: number, y: number, v: number, f: number, hinge: 0 | 1 = 0): void {
    const k = DOOR_KINDS.findIndex((d) => d.name === kind);
    if (k < 0) throw new Error(`structures: no door kind '${kind}'`);
    const wf = this.facing(f);
    this.set(u, y, v, doorId(k, wf, hinge, false, false));
    this.set(u, y + 1, v, doorId(k, wf, hinge, false, true));
  }

  /** A bed with its foot at (u, v) and its head one step along local `f`. */
  bed(u: number, y: number, v: number, f: number): void {
    const [du, dv] = LOCAL_DIR[f & 3];
    this.set(u, y, v, BED_FOOT_ID);
    this.set(u + du, y, v + dv, BED_HEAD_ID);
  }

  /** Stamp a blueprint with its (u = 0, v = 0, layer 0) corner at (u, y, v). */
  stamp(bp: Blueprint, u: number, y: number, v: number, roles: Roles = {}): void {
    for (let l = 0; l < bp.layers.length; l++) {
      const rows = bp.layers[l];
      for (let r = 0; r < rows.length; r++) {
        for (let c = 0; c < rows[r].length; c++) {
          const ch = rows[r][c];
          if (ch === ' ') continue;
          const cell = bp.key[ch];
          const fill = typeof cell === 'string' ? roles[cell] : cell;
          if (fill === undefined) throw new Error(`blueprint: role '${cell}' not given`);
          this.set(u + c, y + l, v + r, fill);
        }
      }
    }
  }
}

// --- blueprints ------------------------------------------------------------------

/** Named stand-ins a blueprint can use instead of fixed blocks ('wall', 'roof'),
 *  so one drawing serves every village style. */
export type Roles = Record<string, Fill>;

/** What a blueprint character means: a block, a role name, or a function. */
export type Cell = Fill | string;

export interface Blueprint {
  /** Columns (u), rows (v), layers (y). */
  w: number; d: number; h: number;
  key: Readonly<Record<string, Cell>>;
  layers: readonly (readonly string[])[];
}

/**
 * An ASCII building. `layers` run bottom to top; in each layer the first row
 * is v = 0 (the front) and the first column u = 0. A space keeps whatever is
 * there; every other character must be in `key`. '.' is air unless the key
 * says otherwise.
 *
 *   blueprint({ '#': block('Cobblestone'), P: 'wall' }, [
 *     ['###', '###'],   // y = 0
 *     ['P.P', 'PPP'],   // y = 1
 *   ])
 */
export function blueprint(key: Record<string, Cell>, layers: string[][]): Blueprint {
  const k: Record<string, Cell> = { '.': AIR, ...key };
  const d = layers[0].length, w = layers[0][0].length;
  layers.forEach((rows, l) => {
    if (rows.length !== d) throw new Error(`blueprint: layer ${l} has ${rows.length} rows, want ${d}`);
    rows.forEach((row, r) => {
      if (row.length !== w) throw new Error(`blueprint: layer ${l} row ${r} is ${row.length} wide, want ${w}`);
      for (const ch of row) if (ch !== ' ' && !(ch in k)) throw new Error(`blueprint: '${ch}' is not in the key`);
    });
  });
  return { w, d, h: layers.length, key: k, layers };
}

/** Fill helpers for blueprint keys. */
export const cell = {
  /** A random choice per block, weighted: cell.mix([[block('Cobblestone'), 3], [block('Mossy Cobblestone'), 1]]). */
  mix(choices: ReadonlyArray<readonly [number, number]>): Fill {
    const total = choices.reduce((s, c) => s + c[1], 0);
    return (b) => {
      let r = b.rnd() * total;
      for (const [id, wgt] of choices) if ((r -= wgt) < 0) return id;
      return choices[choices.length - 1][0];
    };
  },
  chest: (f: number): Fill => (b, u, y, v) => b.chest(u, y, v, f),
  furnace: (f: number): Fill => (b, u, y, v) => b.furnace(u, y, v, f),
  /** Writes both halves: leave the cell above as ' ' in the next layer. */
  door: (kind: string, f: number, hinge: 0 | 1 = 0): Fill => (b, u, y, v) => b.door(kind, u, y, v, f, hinge),
  /** Foot here, head one step along f: leave the head's cell as ' '. */
  bed: (f: number): Fill => (b, u, y, v) => b.bed(u, y, v, f),
};
